import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ScribbleUnderline from './ScribbleUnderline';

const features = [
  { title: 'Calendar & scheduling', description: 'Claw manages your calendar like a pro. Book, move, and cancel appointments with a simple message. Conflicts, reminders, and buffer time — all handled.', icon: FiIcons.FiCalendar, tag: 'Day 1', accent: '#2D6A8F', accentLight: '#EBF2F9', accentBorder: '#C8DDE9', note: 'avg 3 scheduling tasks/day sorted', emoji: '📅' },
  { title: 'Messages & communication', description: 'Claw drafts replies, summarises threads, and helps you stay on top of WhatsApp, email, and messages — in your voice, on your behalf.', icon: FiIcons.FiMessageCircle, tag: 'Day 1–2', accent: '#5C4A2A', accentLight: '#F5F0E8', accentBorder: '#CAAA8A', note: 'sounds like you by day 7', emoji: '💬' },
  { title: 'Reminders & daily life', description: 'Never forget a birthday, a call, or a bill. Claw sets smart reminders and gently nudges you at the right moment — across whatever app you use.', icon: FiIcons.FiBell, tag: 'Day 1', accent: '#3D7A5E', accentLight: '#EBF5F0', accentBorder: '#8ACAAA', note: 'reminders that actually land', emoji: '🔔' },
  { title: 'Travel & logistics', description: 'Planning a trip? Claw researches flights, hotels, and restaurants. It builds itineraries, tracks bookings, and keeps everything in one place.', icon: FiIcons.FiCompass, tag: 'Day 2–3', accent: '#7A2D6A', accentLight: '#F5EBF5', accentBorder: '#CAA8CA', note: 'from search to itinerary in minutes', emoji: '✈️' },
];

const processSteps = [
  { step: 'Quick call', time: 'Day 0' },
  { step: 'Claw goes live', time: 'Day 1' },
  { step: 'Learning your style', time: 'Day 2–4' },
  { step: 'Feels like yours', time: 'Day 7' },
];

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' });
};

const FeatureCard = ({ feature, index }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className="card-premium rounded-2xl p-7 flex flex-col group relative overflow-hidden">
    <div className="absolute top-0 right-0 w-28 h-28 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-br-2xl"
      style={{ background: `radial-gradient(circle at top right,${feature.accentLight},transparent 70%)` }} />
    <div className="font-mono-custom text-[9px] tracking-[0.25em] mb-4 font-semibold uppercase" style={{ color: feature.accent }}>
      {feature.tag}
    </div>
    <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 flex-shrink-0 text-xl"
      style={{ background: feature.accentLight, border: `1px solid ${feature.accentBorder}` }}>
      {feature.emoji}
    </div>
    <h3 className="text-base font-semibold text-[#1A1A18] mb-3 leading-snug">{feature.title}</h3>
    <p className="text-[#6B6560] leading-relaxed text-sm flex-grow mb-4">{feature.description}</p>
    <div className="pt-3 border-t border-[#EDE9E4]">
      <p className="font-handwritten text-sm" style={{ color: feature.accent, transform: 'rotate(-0.5deg)' }}>
        ✎ {feature.note}
      </p>
    </div>
    <div className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-500"
      style={{ background: `linear-gradient(to right,${feature.accent}30,transparent)` }} />
  </motion.div>
);

const Features = () => (
  <section id="capabilities" className="py-32 bg-white relative">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <div className="tag-pill mx-auto mb-5 w-fit">What Claw does</div>
        <h2 className="text-3xl md:text-5xl font-extrabold text-[#1A1A18] mb-3 leading-tight tracking-tight" style={{ letterSpacing: '-0.02em' }}>
          Everything a great assistant<br />
          <span className="gradient-text-accent">actually handles.</span>
        </h2>
        <ScribbleUnderline color="rgba(45,106,143,0.25)" width={260} className="mx-auto mb-5" />
        <p className="text-lg text-[#6B6560] leading-relaxed">
          Claw learns how you work, what you like, and how you communicate — then handles the stuff that fills your day.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {features.map((f, i) => <FeatureCard key={i} feature={f} index={i} />)}
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        transition={{ delay: 0.2 }}
        className="mt-12 px-8 py-5 rounded-2xl bg-[#F8F6F3] border border-[#E8E2DA] flex flex-wrap justify-around gap-5">
        {processSteps.map((item, i) => (
          <div key={item.step} className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-white border border-[#E8E2DA] flex items-center justify-center flex-shrink-0 shadow-sm">
              <span className="text-[10px] font-bold text-[#6B6560]">{i + 1}</span>
            </div>
            <div>
              <span className="text-sm font-semibold text-[#1A1A18] whitespace-nowrap block">{item.step}</span>
              <span className="text-[10px] font-mono-custom text-[#9E9690]">{item.time}</span>
            </div>
            {i < processSteps.length - 1 && <span className="text-[#C8C0B4] hidden sm:block ml-2">→</span>}
          </div>
        ))}
      </motion.div>

      {/* Soft CTA */}
      <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        transition={{ delay: 0.3 }} className="mt-10 text-center">
        <button onClick={() => scrollToSection('pricing')}
          className="inline-flex items-center gap-2 text-[#2D6A8F] font-semibold text-sm hover:text-[#1E4D7A] transition-colors group">
          See pricing →
          <SafeIcon icon={FiIcons.FiArrowRight} className="text-xs group-hover:translate-x-0.5 transition-transform" />
        </button>
      </motion.div>
    </div>
  </section>
);

export default Features;